import React, { useState, useCallback, createContext, useContext, useEffect } from 'react';

// ==================== DATA ====================
const presuppositionTypes = {
  Temporal: {
    words: ['Before', 'Since', 'As', 'Once again', 'Sooner or later', 'After', 'During', 'While', 'Now', 'Then', 'Once', 'Prior', 'When'],
    description: 'Time-based presuppositions that assume an action will occur'
  },
  Awareness: {
    words: ['Begin', 'Notice', 'Comprehend', 'Realize', 'Discover', 'Continue', 'Feel', 'Sense', 'Experience', 'Become aware', 'Perceive', 'Understand', 'Imagine', 'Know', 'Believe'],
    description: 'Presupposes the listener will gain awareness of something'
  },
  Adverb: {
    words: ['Luckily', 'Happily', 'Simply', 'Surely', 'Obviously', 'Frankly', 'Certainly', 'Remarkably', 'Fortunately', 'Naturally', 'Easily', 'Definitely', 'Undoubtedly', 'Quickly'],
    description: 'Describes HOW something will happen, presupposing it will'
  },
  Automatic: {
    words: ['Automatically', 'Second nature', 'Constantly', 'Involuntarily', 'Continuously', 'Spontaneously', 'Instinctively', 'Steadily', 'Almost magically', 'Unconsciously', 'Even without thinking'],
    description: 'Assumes the outcome will happen automatically'
  }
};

const modalOperators = [
  'Must', 'Can', 'May', 'Try', 'Intend to', 'Have to', 'Supposed to', 'Able to',
  'Pretend to', 'Ought to', 'Decide to', 'Wish to', 'Got to', 'Need to', "Let's", 'Want to',
  'Could', 'Choose to', 'Would', 'Will you', 'You could', 'You would', 'Should', 'Will'
];

const tieDowns = [
  "Isn't it?", "Wouldn't you?", "Wasn't it?", "Aren't they?", "Won't you?",
  "Didn't you?", "Does that work for you?", "Don't you agree?", "Make sense?",
  "Doesn't that make sense?", "Right?", "Don't you think?", "Wouldn't that be great?"
];

const conditionalBindWords = [
  'Causes', 'Forces', 'Allows', 'Resolves', 'Creates', 'Makes',
  'Empowers', 'Generates', 'Motivates', 'Shows', 'Leads', 'Compels',
  'Drives', 'Proves', 'Helps', 'Means', 'Justifies'
];

const levelShiftPhrases = [
  'What I think you are saying is…',
  'The real issue is…',
  'What it sounds like you are saying is…',
  'What I hear you saying is…',
  'I believe you are asking…',
  'The real question I believe is…'
];

const directionExamples = [
  'benefits of working with our team',
  'our marketing system',
  'our VIP buyer program',
  'our negotiation skills',
  'our market knowledge',
  'our communication process',
  'our track record',
  'the current market',
  'our pricing strategy',
  'our home search process'
];

const doubleBindTimeOptions = [
  'Tuesday or Wednesday',
  'this week or next week',
  'today or tomorrow',
  'morning or afternoon',
  'after my current meeting or next week',
  'this afternoon or first thing tomorrow',
  'before the weekend or early next week'
];

const doubleBindClosingPhrases = [
  'which works better for you?',
  'which do you prefer?',
  'which makes more sense?',
  'which fits your schedule?',
  'what works best?'
];

// ==================== PROMPT HELPERS ====================
const STYLE_RULES = `STYLE & GRAMMAR (mandatory):
- You are writing for a top-producing real estate agent talking to a client face-to-face. The output must sound like a real human conversation — never like a script lifted from a sales manual.
- Output MUST be grammatically correct standard English.
- Speak directly to the client in second person ("you", "your"). Subjects must be explicit — sentences must not start with a bare verb phrase like "Intend to…" or "Must…".
- No corporate jargon, no hedging ("I just wanted to…", "I'd love to…"), no overused real estate clichés, no universal qualifiers ("always", "never", "everyone", "all clients").
- READ-ALOUD TEST: Mentally read your output aloud. If any clause sounds awkward, choppy, stitched-together, or like a robot reading a script — rewrite it from scratch.
- COMMON-SENSE CHECK: Would a real, experienced agent actually say this exact sentence to a real client? If not, rewrite.

OUTPUT FORMAT:
- Return ONLY the spoken script. No preamble, no labels (unless the framework requires step labels like "Step 1:" or "A:"), no surrounding quotation marks, no explanation.`;

function embeddedCommandRule(command) {
  if (!command || !command.trim()) return '';
  const trimmed = command.trim();
  const upper = trimmed.toUpperCase();
  return `EMBEDDED COMMAND — CRITICAL:
- The phrase "${trimmed}" is the embedded command. Render it EXACTLY as …${upper}… in the output — uppercase letters, with the real ellipsis character … (NOT three periods "...").
- The command MUST fit grammatically into the sentence. Reshape the surrounding sentence so the words inside the ellipses function as a real grammatical unit (e.g. a verb phrase, an action) within the sentence — not pasted in awkwardly.
- GRAMMAR TEST: If you removed the "…" markers, the resulting sentence must still be a single complete, grammatical English sentence. If it isn't, rewrite.
- You may add helper words BEFORE the ellipses (e.g. "to", "you'll", "we should") so the command attaches cleanly. Keep the words INSIDE the ellipses unchanged in meaning, just uppercased.`;
}

// Post-process: guarantee embedded commands are uppercase and use real …
function normalizeEmbeddedCommands(text) {
  if (!text) return text;
  // Convert "..." to "…" first
  const withEllipsis = text.replace(/\.{3,}/g, '…');
  // Uppercase content between ellipsis pairs
  return withEllipsis.replace(/…([^…\n]*?)…/g, (_, inner) => `…${inner.trim().toUpperCase()}…`);
}

function tieDownRule(add) {
  return add ? 'TIE-DOWN: End with a short confirming question like "make sense?", "right?", or "don\'t you agree?". Attach it naturally with proper punctuation.' : '';
}

// ==================== SCRIPT CONTEXT ====================
const ScriptContext = createContext(null);

function ScriptProvider({ resetKey, children }) {
  const [script, setScript] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setScript('');
    setError('');
    setIsLoading(false);
  }, [resetKey]);

  const generate = useCallback(async (prompt) => {
    setIsLoading(true);
    setError('');
    setScript('');
    try {
      const response = await fetch('/api/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const data = await response.json();
      if (!response.ok) {
        const msg = typeof data?.error === 'string'
          ? data.error
          : data?.error?.message || `Request failed (${response.status})`;
        setError(msg);
        return;
      }
      if (data?.content?.[0]?.text) {
        setScript(normalizeEmbeddedCommands(data.content[0].text));
      } else {
        setError('Unexpected response from API.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <ScriptContext.Provider value={{ script, isLoading, error, setError, generate }}>
      {children}
    </ScriptContext.Provider>
  );
}

function useScriptGenerator() {
  return useContext(ScriptContext);
}

// ==================== SHARED COMPONENTS ====================
function WordChips({ words, selected, onSelect }) {
  return (
    <div className="flex flex-wrap gap-2">
      {words.map((word) => {
        const active = selected === word;
        return (
          <button
            key={word}
            type="button"
            onClick={() => onSelect(word)}
            aria-pressed={active}
            className={`px-3 py-1.5 rounded-full text-xs font-medium tracking-wide backdrop-blur-md border transition-all duration-200 ${
              active
                ? 'bg-neon/15 border-neon text-neon shadow-neon'
                : 'bg-white/[0.04] border-white/10 text-zinc-300 hover:border-white/25 hover:text-white'
            }`}
          >
            {word}
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="block text-[11px] font-mono font-semibold text-zinc-400 uppercase tracking-[0.12em] mb-2">
        {label}{required && <span className="text-neon ml-1">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-zinc-500 mt-1.5">{hint}</p>}
    </div>
  );
}

function TextInput(props) {
  return (
    <input
      type="text"
      {...props}
      className={`w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-neon/60 focus:bg-white/[0.05] transition-colors ${props.className || ''}`}
    />
  );
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <span className={`relative w-5 h-5 rounded border transition-colors flex items-center justify-center ${
        checked ? 'bg-neon border-neon' : 'bg-white/[0.03] border-white/15 group-hover:border-white/30'
      }`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
        {checked && (
          <svg className="w-3 h-3 text-ink" viewBox="0 0 12 12" fill="none">
            <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">{label}</span>
    </label>
  );
}

function PillToggle({ options, value, onChange }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={`px-3.5 py-2 rounded-full text-xs font-medium tracking-wide border backdrop-blur-md transition-all ${
              active
                ? opt.warn
                  ? 'bg-orange-500/15 border-orange-400 text-orange-300'
                  : 'bg-neon/15 border-neon text-neon'
                : 'bg-white/[0.04] border-white/10 text-zinc-300 hover:border-white/25 hover:text-white'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function GenerateButton({ onClick, disabled, isLoading }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className="group relative w-full overflow-hidden rounded-full bg-white/[0.06] backdrop-blur-lg border border-neon/40 px-8 py-3.5 flex items-center justify-center gap-2 transition-all duration-300 hover:-translate-y-0.5 hover:bg-neon/15 hover:border-neon hover:shadow-neon disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:bg-white/[0.06] disabled:hover:shadow-none"
    >
      <span className="relative z-10 text-neon font-medium tracking-[0.08em] text-sm uppercase">
        {isLoading ? 'Generating…' : 'Generate Script'}
      </span>
      {!isLoading && (
        <svg className="relative z-10 w-4 h-4 text-neon transition-transform duration-300 group-hover:translate-x-1" viewBox="0 0 16 16" fill="none">
          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full transition-transform duration-1000 group-hover:translate-x-full" />
    </button>
  );
}

function ErrorBanner({ error }) {
  if (!error) return null;
  return (
    <div role="alert" className="p-3 bg-red-500/10 border border-red-500/40 rounded-lg text-red-300 text-sm">
      {error}
    </div>
  );
}

function InfoCard({ children }) {
  return (
    <div className="bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-xl p-4 text-sm text-zinc-300">
      {children}
    </div>
  );
}

function OutputPanel() {
  const { script, isLoading } = useScriptGenerator();

  if (isLoading) {
    return (
      <div className="rounded-xl border border-neon/30 bg-gradient-to-br from-neon/[0.04] to-transparent backdrop-blur-md overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5">
          <span className="w-1.5 h-1.5 rounded-full bg-neon animate-glow" />
          <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.18em]">Generating…</h3>
        </div>
        <div className="px-5 py-4 space-y-2">
          <div className="h-3 rounded bg-white/10 animate-pulse" />
          <div className="h-3 rounded bg-white/10 animate-pulse w-11/12" />
          <div className="h-3 rounded bg-white/10 animate-pulse w-4/5" />
          <div className="h-3 rounded bg-white/10 animate-pulse w-2/3" />
        </div>
      </div>
    );
  }

  if (script) {
    return (
      <div className="rounded-xl border border-neon/30 bg-gradient-to-br from-neon/[0.04] to-transparent backdrop-blur-md overflow-hidden">
        <div className="flex justify-between items-center px-5 py-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-neon animate-glow" />
            <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.18em]">Generated Script</h3>
          </div>
          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(script)}
            className="px-3 py-1 text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-neon border border-white/10 hover:border-neon/40 rounded-full transition-colors"
          >
            Copy
          </button>
        </div>
        <div className="px-5 py-4">
          <p className="text-white leading-relaxed whitespace-pre-wrap font-light">{script}</p>
        </div>
      </div>
    );
  }

  // Empty state — desktop-only placeholder
  return (
    <div className="hidden lg:block rounded-xl border border-dashed border-white/10 bg-white/[0.015] backdrop-blur-md px-6 py-10 text-center">
      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full border border-neon/30 bg-neon/5 mb-4">
        <svg className="w-5 h-5 text-neon" viewBox="0 0 24 24" fill="none">
          <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <h3 className="text-sm font-medium text-white mb-1">Your script will appear here</h3>
      <p className="text-xs text-zinc-500 leading-relaxed max-w-xs mx-auto">
        Fill in the form on the left and press <span className="text-neon">Generate Script</span> to craft a script using this NLP pattern.
      </p>
    </div>
  );
}

// ==================== TAB COMPONENTS ====================

function PresuppositionsTab() {
  const [presupType, setPresupType] = useState('Temporal');
  const [selectedWord, setSelectedWord] = useState('Before');
  const [presupposing, setPresupposing] = useState('');
  const [direction, setDirection] = useState('');
  const [embeddedCommand, setEmbeddedCommand] = useState('');
  const [addTieDown, setAddTieDown] = useState(false);
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const handleTypeChange = (type) => {
    setPresupType(type);
    setSelectedWord(presuppositionTypes[type].words[0]);
  };

  const onGenerate = () => {
    if (!presupposing.trim()) {
      setError('Please enter what you are presupposing');
      return;
    }
    const prompt = `You are a real estate sales communication coach. Generate a 1-2 sentence script for an agent to say directly to their client.

${STYLE_RULES}

TASK: Build a natural sentence that uses the word "${selectedWord}" (a ${presupType.toLowerCase()} presupposition — ${presuppositionTypes[presupType].description.toLowerCase()}) to presuppose that the client will: ${presupposing}.
${direction ? `FOCUS: Tie the presupposition to "${direction}".` : ''}

${embeddedCommandRule(embeddedCommand)}

${tieDownRule(addTieDown)}

Good example: "Once you discover how much our marketing system does for you, you'll naturally want to …LIST YOUR HOME WITH US… — make sense?"
(Note: removing the ellipses still yields a complete grammatical sentence.)`;
    generate(prompt);
  };

  return (
    <div className="space-y-6">
      <Field label="Presupposition Type" hint={presuppositionTypes[presupType].description}>
        <select
          value={presupType}
          onChange={(e) => handleTypeChange(e.target.value)}
          className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-neon/60"
        >
          {Object.keys(presuppositionTypes).map((type) => (
            <option key={type} value={type} className="bg-ink">{type}</option>
          ))}
        </select>
      </Field>

      <Field label="Select Word">
        <WordChips words={presuppositionTypes[presupType].words} selected={selectedWord} onSelect={setSelectedWord} />
      </Field>

      <Field label="What You Are Presupposing" required>
        <TextInput
          value={presupposing}
          onChange={(e) => setPresupposing(e.target.value)}
          placeholder="e.g., the client will work with me"
        />
      </Field>

      <Field label="Direction / Focus">
        <WordChips
          words={directionExamples}
          selected={direction}
          onSelect={(w) => setDirection(direction === w ? '' : w)}
        />
        <TextInput
          value={direction}
          onChange={(e) => setDirection(e.target.value)}
          placeholder="Or type your own…"
          className="mt-2"
        />
      </Field>

      <Field label="Embedded Command" hint="Will be formatted as …COMMAND…">
        <TextInput
          value={embeddedCommand}
          onChange={(e) => setEmbeddedCommand(e.target.value)}
          placeholder="e.g., sign the contract"
        />
      </Field>

      <Checkbox checked={addTieDown} onChange={(e) => setAddTieDown(e.target.checked)} label="Add tie-down question" />

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

function EmbeddedCommandsTab() {
  const [context, setContext] = useState('');
  const [command, setCommand] = useState('');
  const [modalOperator, setModalOperator] = useState('');
  const [addTieDown, setAddTieDown] = useState(false);
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const onGenerate = () => {
    if (!command.trim()) {
      setError('Please enter a command');
      return;
    }
    const prompt = `You are a top-producing real estate agent talking to a client. Generate a 1-2 sentence script you would actually say out loud in a real conversation — warm, confident, conversational. NEVER robotic or instructional.

${STYLE_RULES}

${embeddedCommandRule(command)}

${modalOperator ? `MODAL OPERATOR — CRITICAL:
- "${modalOperator}" is a SETUP phrase, not a sentence opener. It MUST be attached to the subject "you" so it sounds like natural conversation.
- Use patterns like: "If you ${modalOperator.toLowerCase()}…", "When you ${modalOperator.toLowerCase()}…", "Since you ${modalOperator.toLowerCase()}…", or "You ${modalOperator.toLowerCase()}…".
- NEVER start the sentence with the bare modal operator (e.g. "Intend to…", "Must…", "Have to…") — that sounds like a robotic instruction.` : ''}

CONTEXT (the situation): ${context || 'General real estate conversation.'}

${tieDownRule(addTieDown)}

GOOD examples (natural agent speech):
- "If you really want to sell this home quickly, we need to …PRICE IT AGGRESSIVELY THIS WEEK… — make sense?"
- "When you decide to …WORK WITH ME…, you'll get full access to our VIP buyer program — right?"
- "Since you want top dollar for your home, the smart move is to …LIST IT THIS FRIDAY… — don't you agree?"

BAD examples (DO NOT produce — they sound robotic):
- "Intend to …REDUCE THE PRICE… this week…" (no subject — sounds like a command)
- "Must …CALL THE LENDER…" (cold instruction)
- "Have to …SIGN THE CONTRACT…" (missing "you")`;
    generate(prompt);
  };

  return (
    <div className="space-y-6">
      <InfoCard>
        <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.15em] mb-3">4 Elements of Embedded Commands</h3>
        <ol className="text-zinc-300 text-sm space-y-1.5 list-decimal list-inside">
          <li>It is a COMMAND</li>
          <li>It is EMBEDDED in the middle</li>
          <li>Ends on a DOWNSWING</li>
          <li>Pause BEFORE and AFTER</li>
        </ol>
      </InfoCard>

      <Field label="Context">
        <TextInput value={context} onChange={(e) => setContext(e.target.value)} placeholder="e.g., Buyer consultation" />
      </Field>

      <Field label="Command to Embed" required>
        <TextInput value={command} onChange={(e) => setCommand(e.target.value)} placeholder="e.g., work with me" />
      </Field>

      <Field label="Modal Operator">
        <WordChips words={modalOperators.slice(0, 12)} selected={modalOperator} onSelect={(w) => setModalOperator(modalOperator === w ? '' : w)} />
      </Field>

      <Checkbox checked={addTieDown} onChange={(e) => setAddTieDown(e.target.checked)} label="Add tie-down" />

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

function ModalOperatorsTab() {
  const [selectedOperator, setSelectedOperator] = useState('');
  const [action, setAction] = useState('');
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const onGenerate = () => {
    if (!selectedOperator || !action.trim()) {
      setError('Please select a modal operator and enter an action');
      return;
    }
    const prompt = `You are a top-producing real estate agent talking to a client. Generate a 1-2 sentence script you would actually say out loud in a real conversation — warm, confident, and conversational. NEVER robotic or instructional.

${STYLE_RULES}

MODAL OPERATOR — CRITICAL:
- "${selectedOperator}" is a SETUP phrase. It MUST be attached to the subject "you" so it sounds like natural conversation.
- Use natural patterns: "If you ${selectedOperator.toLowerCase()}…", "When you ${selectedOperator.toLowerCase()}…", "You ${selectedOperator.toLowerCase()}…".
- NEVER start with the bare modal (e.g. "Intend to…", "Must…") — that sounds robotic.

DESIRED ACTION: ${action}
Mark the action phrase as …THE ACTION… (uppercase, in real ellipsis characters …). The action must fit grammatically — removing the ellipses must still yield a complete grammatical English sentence.

GOOD examples:
- "If you want to …BUY A HOME SOON…, you should …SEND ME YOUR WISHLIST TODAY…"
- "When you decide to …WORK WITH ME…, you'll get our full marketing package."
- "Since you intend to move by summer, you need to …START LOOKING THIS WEEK…"

BAD examples (DO NOT produce):
- "Intend to …BUY A HOME…" (no subject)
- "Must …WRITE AN OFFER…" (cold instruction)`;
    generate(prompt);
  };

  return (
    <div className="space-y-6">
      <InfoCard>
        <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.15em] mb-2">Modal Operators</h3>
        <p className="text-zinc-300 text-sm">Setup words that create a "Listen to Me Now" moment. Words of possibility or necessity.</p>
      </InfoCard>

      <Field label="Select Modal Operator">
        <WordChips words={modalOperators} selected={selectedOperator} onSelect={setSelectedOperator} />
      </Field>

      <Field label="Action You Want" required>
        <TextInput value={action} onChange={(e) => setAction(e.target.value)} placeholder="e.g., schedule a showing" />
      </Field>

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

function TieDownsTab() {
  const [selectedTieDown, setSelectedTieDown] = useState('');
  const [statement, setStatement] = useState('');
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const onGenerate = () => {
    if (!statement.trim()) {
      setError('Please enter a statement');
      return;
    }
    const prompt = `You are a real estate sales communication coach. Generate a 1-2 sentence script that ends with a tie-down question.

${STYLE_RULES}

STATEMENT (rewrite into natural agent speech directed at the client): ${statement}
TIE-DOWN: ${selectedTieDown ? `End with "${selectedTieDown}".` : 'End with whichever tie-down fits best ("make sense?", "don\'t you agree?", "right?", etc.).'}

Good example: "You should write a strong offer on this home — don't you agree?"`;
    generate(prompt);
  };

  return (
    <div className="space-y-6">
      <Field label="Select Tie-Down">
        <WordChips words={tieDowns} selected={selectedTieDown} onSelect={setSelectedTieDown} />
      </Field>

      <Field label="Your Statement" required>
        <TextInput value={statement} onChange={(e) => setStatement(e.target.value)} placeholder="e.g., We should write a strong offer" />
      </Field>

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

function PacingLeadingTab() {
  const [paces, setPaces] = useState(['', '', '', '', '', '']);
  const [leads, setLeads] = useState(['', '', '', '']);
  const [embeddedCommand, setEmbeddedCommand] = useState('');
  const [addTieDown, setAddTieDown] = useState(true);
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const updatePace = (i, v) => setPaces(p => p.map((x, idx) => idx === i ? v : x));
  const updateLead = (i, v) => setLeads(l => l.map((x, idx) => idx === i ? v : x));

  const onGenerate = () => {
    const validPaces = paces.filter(p => p.trim());
    const validLeads = leads.filter(l => l.trim());
    if (validPaces.length === 0 || validLeads.length === 0) {
      setError('Please enter at least one pace and one lead');
      return;
    }
    const prompt = `You are a real estate sales communication coach. Generate a pacing-and-leading script.

${STYLE_RULES}

PATTERN: Acknowledge true things about the client (paces) to build agreement, then guide them toward what you want (leads). Flow should be conversational, not robotic — do NOT use a "you said X, you said Y, therefore Z" structure.

PACES (true things the client agrees with): ${validPaces.map(p => `"${p}"`).join('; ')}
LEADS (what you want them to accept): ${validLeads.map(l => `"${l}"`).join('; ')}

${embeddedCommandRule(embeddedCommand)}

${tieDownRule(addTieDown)}

Aim for 2-3 sentences. Weave the paces in naturally, then transition into the leads using words like "so", "since", "given that", or "with that in mind".

Good example: "Since you're ready to move by summer and you need at least three bedrooms in a great school district, you'll want to …START LOOKING THIS WEEK… — make sense?"`;
    generate(prompt);
  };

  return (
    <div className="space-y-6">
      <InfoCard>
        <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.15em] mb-2">Pacing &amp; Leading</h3>
        <p className="text-zinc-300 text-sm">
          <span className="text-neon">PACE</span> = True statement they agree with →{' '}
          <span className="text-sky-300">LEAD</span> = What you want them to accept
        </p>
        <p className="text-zinc-500 text-xs mt-1.5 font-mono">Pace · Pace · Pace → Lead → Tie-Down → Close</p>
      </InfoCard>

      <Field label="Paces (true statements about client)">
        <div className="space-y-2">
          {paces.map((pace, i) => (
            <TextInput
              key={i}
              value={pace}
              onChange={(e) => updatePace(i, e.target.value)}
              placeholder={`Pace ${i + 1}${i < 2 ? '' : ' (optional)'}: e.g., You want to move by summer`}
            />
          ))}
        </div>
      </Field>

      <Field label="Leads (what you want them to accept)">
        <div className="space-y-2">
          {leads.map((lead, i) => (
            <TextInput
              key={i}
              value={lead}
              onChange={(e) => updateLead(i, e.target.value)}
              placeholder={`Lead ${i + 1}${i > 0 ? ' (optional)' : ''}: e.g., start looking right away`}
            />
          ))}
        </div>
      </Field>

      <Field label="Embedded Command" hint="Will be formatted as …COMMAND…">
        <TextInput
          value={embeddedCommand}
          onChange={(e) => setEmbeddedCommand(e.target.value)}
          placeholder="e.g., start looking right away"
        />
      </Field>

      <Checkbox checked={addTieDown} onChange={(e) => setAddTieDown(e.target.checked)} label="Add tie-down" />

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

function ConditionalBindsTab() {
  const [pace, setPace] = useState('');
  const [lead, setLead] = useState('');
  const [selectedWord, setSelectedWord] = useState('');
  const [addTieDown, setAddTieDown] = useState(true);
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const onGenerate = () => {
    if (!pace.trim() || !lead.trim()) {
      setError('Please enter both pace and lead');
      return;
    }
    const prompt = `You are a real estate sales communication coach. Generate a conditional bind script.

${STYLE_RULES}

PATTERN: "If [pace], then [lead]." If the client accepts the pace, they must also accept the lead.

PACE (what the client agrees to): "${pace}"
LEAD (what you want them to accept): "${lead}"
${selectedWord ? `BINDING WORD: Use "${selectedWord.toLowerCase()}" (or a natural variant) to link pace to lead.` : ''}

Mark the lead as …THE LEAD… (uppercase, in ellipses). The lead must fit grammatically inside the sentence — removing the ellipses must still yield a complete grammatical English sentence.

${tieDownRule(addTieDown)}

Good example: "If selling your home quickly really matters to you, then we have to …PRICE IT AGGRESSIVELY… — don't you agree?"`;
    generate(prompt);
  };

  return (
    <div className="space-y-6">
      <InfoCard>
        <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.15em] mb-2">Conditional Binds</h3>
        <p className="text-zinc-300 text-sm">
          If the <span className="text-neon">PACE</span> is accepted, then the{' '}
          <span className="text-sky-300">LEAD</span> must also be accepted.
        </p>
      </InfoCard>

      <Field label="Pace (what they agree to)" required>
        <TextInput value={pace} onChange={(e) => setPace(e.target.value)} placeholder="e.g., selling quickly is important" />
      </Field>

      <Field label="Lead (what you want)" required>
        <TextInput value={lead} onChange={(e) => setLead(e.target.value)} placeholder="e.g., price it aggressively" />
      </Field>

      <Field label="Binding Words">
        <WordChips words={conditionalBindWords} selected={selectedWord} onSelect={(w) => setSelectedWord(selectedWord === w ? '' : w)} />
      </Field>

      <Checkbox checked={addTieDown} onChange={(e) => setAddTieDown(e.target.checked)} label="Add tie-down" />

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

function DoubleBindsTab() {
  const [bindType, setBindType] = useState('time');
  const [timeOption, setTimeOption] = useState('');
  const [closingPhrase, setClosingPhrase] = useState('which works better for you?');
  const [optionA, setOptionA] = useState('');
  const [optionB, setOptionB] = useState('');
  const [pace, setPace] = useState('');
  const [context, setContext] = useState('');
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const onGenerate = () => {
    let prompt = '';

    if (bindType === 'time') {
      if (!timeOption && !optionA) {
        setError('Please select a time option or enter custom options');
        return;
      }
      const timeChoices = timeOption || `${optionA} or ${optionB}`;
      prompt = `You are a real estate sales communication coach. Generate a time-based double bind for closing/scheduling.

${STYLE_RULES}

PATTERN: Frame the meeting as already happening, then offer two time options — both lead to the appointment.

TIME CHOICES: ${timeChoices}
CLOSING PHRASE: "${closingPhrase}"
${context ? `CONTEXT: ${context}` : ''}

Keep it to 1-2 sentences. End with the closing phrase.

Good examples:
- "Let's get together this week — I have time on Tuesday or Wednesday, which works better for you?"
- "I can come by after my current meeting or first thing tomorrow — what works best?"`;
    } else if (bindType === 'standard') {
      if (!optionA.trim() || !optionB.trim()) {
        setError('Please enter both options');
        return;
      }
      prompt = `You are a real estate sales communication coach. Generate a standard double bind script.

${STYLE_RULES}

PATTERN: Both options lead to your goal. Presenting the choice itself creates the assumption that the thing WILL happen.

OPTION A: ${optionA}
OPTION B: ${optionB}
${context ? `CONTEXT: ${context}` : ''}

Keep it to 1-2 sentences. End with a closing phrase such as "which approach makes more sense?", "which do you prefer?", or "which works better?".

Good example: "We can price aggressively to attract multiple offers, or we can list at the top of the market and stay patient — which approach makes more sense to you?"`;
    } else if (bindType === 'lesserEvil') {
      if (!optionA.trim() || !optionB.trim()) {
        setError('Please enter both options');
        return;
      }
      if (!pace.trim()) {
        setError('Please enter a pace statement for Lesser of Two Evils');
        return;
      }
      prompt = `You are a real estate sales communication coach. Generate a "Lesser of Two Evils" double bind script.

${STYLE_RULES}

PATTERN: Open with a PACE (a true statement the client already agrees with) to set up the bind, then offer two options that BOTH have a cost — but BOTH move toward your goal.

PACE (true statement): "${pace}"
OPTION A (has a cost): ${optionA}
OPTION B (also has a cost): ${optionB}
${context ? `CONTEXT: ${context}` : ''}

Use a transition like "so we either…", "with that in mind, we can…". End with a phrase like "which would you prefer?" or "which makes more sense?".

Good example: "You told me you wanted to get your home sold quickly, so we either need to offer some buyer incentives or be willing to make a small price adjustment — which would you prefer?"`;
    }

    generate(prompt);
  };

  const typeOptions = [
    { value: 'time', label: 'Time-Based Close' },
    { value: 'standard', label: 'Standard Double Bind' },
    { value: 'lesserEvil', label: 'Lesser of Two Evils', warn: true },
  ];

  return (
    <div className="space-y-6">
      <InfoCard>
        <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.15em] mb-2">Double Binds</h3>
        <p className="text-zinc-300 text-sm">
          Give them a choice where <span className="text-neon">BOTH options</span> lead to your goal. Either way, you win.
        </p>
      </InfoCard>

      <Field label="Double Bind Type">
        <PillToggle options={typeOptions} value={bindType} onChange={setBindType} />
      </Field>

      {bindType === 'time' && (
        <InfoCard>
          <span className="text-neon font-medium">Perfect for scheduling:</span>{' '}
          <span className="text-zinc-300">"I have time on Tuesday or Wednesday — which works better for you?"</span>
        </InfoCard>
      )}
      {bindType === 'standard' && (
        <InfoCard>
          <span className="text-sky-300 font-medium">Creates an assumption:</span>{' '}
          <span className="text-zinc-300">Both choices assume the thing WILL happen.</span>
        </InfoCard>
      )}
      {bindType === 'lesserEvil' && (
        <div className="bg-orange-500/[0.06] backdrop-blur-md border border-orange-400/30 rounded-xl p-4 text-sm">
          <span className="text-orange-300 font-medium">Uses a PACE first:</span>{' '}
          <span className="text-zinc-300">Both options have a cost, but both move toward your goal.</span>
        </div>
      )}

      {bindType === 'time' && (
        <>
          <Field label="Quick Time Options">
            <WordChips words={doubleBindTimeOptions} selected={timeOption} onSelect={(w) => setTimeOption(timeOption === w ? '' : w)} />
          </Field>
          <div className="text-zinc-600 text-center text-[10px] font-mono tracking-[0.2em] uppercase">— or enter custom —</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextInput
              value={optionA}
              onChange={(e) => { setOptionA(e.target.value); setTimeOption(''); }}
              placeholder="Option A: e.g., after my meeting"
            />
            <TextInput
              value={optionB}
              onChange={(e) => { setOptionB(e.target.value); setTimeOption(''); }}
              placeholder="Option B: e.g., next week"
            />
          </div>
          <Field label="Closing Phrase">
            <WordChips words={doubleBindClosingPhrases} selected={closingPhrase} onSelect={setClosingPhrase} />
          </Field>
        </>
      )}

      {bindType === 'standard' && (
        <>
          <Field label="Option A" required>
            <TextInput value={optionA} onChange={(e) => setOptionA(e.target.value)} placeholder="e.g., price aggressively and get lots of offers" />
          </Field>
          <Field label="Option B" required>
            <TextInput value={optionB} onChange={(e) => setOptionB(e.target.value)} placeholder="e.g., price at top of market and be conservative" />
          </Field>
        </>
      )}

      {bindType === 'lesserEvil' && (
        <>
          <Field label="Pace (true statement they agree with)" required>
            <TextInput value={pace} onChange={(e) => setPace(e.target.value)} placeholder="e.g., You told me you wanted to get your home sold quickly" />
          </Field>
          <Field label="Option A (has a cost)" required>
            <TextInput value={optionA} onChange={(e) => setOptionA(e.target.value)} placeholder="e.g., offer buyer incentives" />
          </Field>
          <Field label="Option B (also has a cost)" required>
            <TextInput value={optionB} onChange={(e) => setOptionB(e.target.value)} placeholder="e.g., offer seller concessions" />
          </Field>
        </>
      )}

      <Field label="Context">
        <TextInput value={context} onChange={(e) => setContext(e.target.value)} placeholder="e.g., Buyer consultation, Listing appointment" />
      </Field>

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

function ObjectionHandlingTab() {
  const [method, setMethod] = useState('random');
  const [objection, setObjection] = useState('');
  const [levelShiftPhrase, setLevelShiftPhrase] = useState('');
  const [militaryOffer, setMilitaryOffer] = useState('');
  const { isLoading, error, setError, generate } = useScriptGenerator();

  const militaryOffers = [
    'VIP Buyer Program access',
    'Free home warranty',
    'Professional photography',
    'Zero cancellation contract',
    'Market analysis report',
    'Priority showing scheduling'
  ];

  const onGenerate = () => {
    if (!objection.trim()) {
      setError('Please enter the objection');
      return;
    }

    if (method === 'arp') {
      const arpPrompt = `You are a real estate sales communication coach. Generate an ARP (Acknowledge / Respond / Pivot) script for handling a RED HERRING objection — one that isn't the real issue.

${STYLE_RULES}

PATTERN — keep it flowing, no pauses between steps, total length 3 short sentences:
A — ACKNOWLEDGE: Briefly repeat back what they said.
R — RESPOND: Answer directly and briefly. Do not dwell.
P — PIVOT: Immediately move to what you want to discuss, offering a choice.

OBJECTION: "${objection}"

Label each step exactly as "A:", "R:", "P:" on its own line.

Good example:
A: So you were wondering if the seller would take less.
R: Honestly, I don't know what they'll take.
P: With that in mind, let's go look at the house — I have time today or tomorrow, which works?`;
      generate(arpPrompt);
      return;
    }

    let handlingMethod = method;
    if (method === 'random') {
      const methods = ['feelFeltFound', 'levelshift', 'military', 'hasThereEver'];
      handlingMethod = methods[Math.floor(Math.random() * methods.length)];
    }

    let step3Instructions = '';
    if (handlingMethod === 'feelFeltFound') {
      step3Instructions = `Step 3 - Handle using FEEL FELT FOUND:
- "I know exactly how you FEEL..."
- "Many of my clients have FELT the exact same way..."
- "But what they FOUND was..." (give the benefit/reframe)`;
    } else if (handlingMethod === 'levelshift') {
      step3Instructions = `Step 3 - Handle using LEVEL SHIFT:
- Move from what you CAN'T control to what you CAN control
${levelShiftPhrase ? `- Use this reframe phrase: "${levelShiftPhrase}"` : '- Use a reframe phrase like "What I hear you saying is..." or "The real question is..."'}`;
    } else if (handlingMethod === 'military') {
      step3Instructions = `Step 3 - Handle using MILITARY PATTERN:
- What would happen if you did it a different way? (present alternative)
- Here's why that different way is a good idea (give benefit)
- Reciprocity: "If you would be willing to do that, I would be willing to ${militaryOffer || 'give you access to our VIP program'}"`;
    } else if (handlingMethod === 'hasThereEver') {
      step3Instructions = `Step 3 - Handle using HAS THERE EVER BEEN A TIME WHEN:
- Ask: "Has there ever been a time when you [made a similar decision] and later wished you had [done differently]?"
- This leverages past mistakes - people hate making the same mistake twice
- Draw a parallel between a generic past mistake and their current situation`;
    }

    const prompt = `You are a real estate sales communication coach. Generate a 4-step objection handling script.

${STYLE_RULES}

FRAMEWORK — label each step exactly as "Step 1:", "Step 2:", "Step 3:", "Step 4:" on its own line. Each step is 1-2 sentences of natural agent speech:

Step 1 — ACKNOWLEDGE & AFFIRM: Never say "I understand" (it breaks rapport). Use "I appreciate that", "I hear you", or "Excellent, I'm glad you brought that up".
Step 2 — ISOLATE: Ask, "Besides that, is there any other reason you wouldn't want to move forward?" (Get a NO so the objection becomes the only obstacle.)
Step 3 — HANDLE using the specific technique below.
Step 4 — CLOSE with a tie-down question that confirms agreement.

${step3Instructions}

OBJECTION: "${objection}"`;
    generate(prompt);
  };

  const methodOptions = [
    { value: 'random', label: '🎲 Random' },
    { value: 'feelFeltFound', label: 'Feel Felt Found' },
    { value: 'levelshift', label: 'Level Shift' },
    { value: 'military', label: 'Military Pattern' },
    { value: 'hasThereEver', label: 'Has There Ever Been' },
    { value: 'arp', label: 'ARP (Red Herrings)', warn: true },
  ];

  return (
    <div className="space-y-6">
      <InfoCard>
        <h3 className="text-[11px] font-mono font-semibold text-neon uppercase tracking-[0.15em] mb-3">4-Step Framework</h3>
        <div className="space-y-1 text-sm text-zinc-300">
          <p><span className="text-neon font-mono">01</span>  Acknowledge &amp; Affirm <span className="text-zinc-500">(NOT "I understand"!)</span></p>
          <p><span className="text-sky-300 font-mono">02</span>  Isolate <span className="text-zinc-500">("Besides that…")</span></p>
          <p><span className="text-yellow-300 font-mono">03</span>  Handle <span className="text-zinc-500">→ using your selected technique</span></p>
          <p><span className="text-purple-300 font-mono">04</span>  Close</p>
        </div>
      </InfoCard>

      <Field label="Step 3 Handling Technique">
        <PillToggle options={methodOptions} value={method} onChange={setMethod} />
      </Field>

      <Field label="Client Objection" required>
        <TextInput value={objection} onChange={(e) => setObjection(e.target.value)} placeholder="e.g., We want to wait for interest rates to drop" />
      </Field>

      {method === 'levelshift' && (
        <Field label="Level Shift Phrase">
          <WordChips words={levelShiftPhrases} selected={levelShiftPhrase} onSelect={(w) => setLevelShiftPhrase(levelShiftPhrase === w ? '' : w)} />
        </Field>
      )}

      {method === 'military' && (
        <Field label="Your Reciprocity Offer">
          <WordChips words={militaryOffers} selected={militaryOffer} onSelect={(w) => setMilitaryOffer(militaryOffer === w ? '' : w)} />
          <TextInput
            value={militaryOffer}
            onChange={(e) => setMilitaryOffer(e.target.value)}
            placeholder="Or type your own offer…"
            className="mt-2"
          />
        </Field>
      )}

      <ErrorBanner error={error} />
      <GenerateButton onClick={onGenerate} isLoading={isLoading} />
    </div>
  );
}

// ==================== TICKER ====================
function Ticker({ items }) {
  const row = (
    <div className="flex items-center gap-3 px-3 shrink-0">
      {items.map((label, i) => (
        <div key={i} className="flex items-center gap-2 bg-white/[0.04] border border-white/10 rounded-full px-3.5 py-1 backdrop-blur-md whitespace-nowrap">
          <span className="w-1 h-1 rounded-full bg-neon" />
          <span className="text-[10px] text-white/85 font-medium tracking-[0.15em] uppercase">{label}</span>
          <span className="text-[10px] text-zinc-500 font-mono">NLP</span>
        </div>
      ))}
    </div>
  );
  return (
    <div
      className="overflow-hidden pointer-events-none"
      style={{
        maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
        WebkitMaskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
      }}
    >
      <div className="flex items-center w-max animate-ticker">
        {row}
        {row}
      </div>
    </div>
  );
}

// ==================== MAIN APP ====================
export default function CFRNLPCoach() {
  const tabs = [
    { id: 'presuppositions', label: 'Presuppositions' },
    { id: 'embedded', label: 'Embedded Commands' },
    { id: 'modal', label: 'Modal Operators' },
    { id: 'tiedowns', label: 'Tie-Downs' },
    { id: 'pacing', label: 'Pacing & Leading' },
    { id: 'conditional', label: 'Conditional Binds' },
    { id: 'doublebinds', label: 'Double Binds' },
    { id: 'objections', label: 'Objection Handling' },
  ];

  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = new URLSearchParams(window.location.search).get('tab');
    return tabs.some((tab) => tab.id === requestedTab) ? requestedTab : 'presuppositions';
  });

  const selectTab = (tabId) => {
    setActiveTab(tabId);
    const url = new URL(window.location.href);
    if (tabId === 'presuppositions') {
      url.searchParams.delete('tab');
    } else {
      url.searchParams.set('tab', tabId);
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  return (
    <div className="min-h-screen bg-ink text-white relative overflow-x-hidden">
      {/* Background layers */}
      <div className="fixed inset-0 bg-grid pointer-events-none z-0" />
      <div className="fixed top-1/3 -left-[20vw] w-[55vw] h-[55vw] bg-neon/10 rounded-full blur-[140px] pointer-events-none z-0 mix-blend-screen" />
      <div className="fixed bottom-0 -right-[15vw] w-[40vw] h-[40vw] bg-sky-500/5 rounded-full blur-[140px] pointer-events-none z-0 mix-blend-screen" />
      <div className="fixed inset-0 scanlines pointer-events-none z-[1] mix-blend-overlay opacity-25" />

      {/* Top ticker */}
      <div className="relative z-30 pt-3 sm:pt-4 pb-2">
        <Ticker items={tabs.map(t => t.label)} />
      </div>

      {/* Header */}
      <header className="relative z-20 px-4 sm:px-6 pt-6 sm:pt-8 pb-5 sm:pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/[0.04] border border-white/10 rounded-full backdrop-blur-md mb-4 sm:mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-neon animate-glow" />
          <span className="text-[10px] font-mono font-semibold text-neon uppercase tracking-[0.2em]">CFR NLP Engine · v1</span>
        </div>
        <h1 className="text-[2rem] sm:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tighter leading-[1.04] text-white max-w-4xl mx-auto">
          Stop pitching.{' '}
          <span className="text-neon drop-shadow-[0_0_30px_rgba(74,222,128,0.45)]">Start persuading.</span>
        </h1>
        <p className="text-zinc-400 mt-3 sm:mt-4 text-sm sm:text-lg font-light max-w-xl mx-auto leading-relaxed px-2">
          AI-crafted real estate scripts built on proven NLP patterns — presuppositions, embedded commands, double binds, and more.
        </p>
      </header>

      {/* Tab bar */}
      <div className="relative z-20 px-3 sm:px-4 mb-5 sm:mb-6">
        <div className="max-w-7xl mx-auto">
          <div
            role="tablist"
            aria-label="NLP techniques"
            className="flex gap-1.5 p-1.5 bg-white/[0.03] border border-white/10 rounded-full backdrop-blur-md overflow-x-auto scrollbar-thin"
          >
            {tabs.map((tab) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={active}
                  aria-controls={`panel-${tab.id}`}
                  id={`tab-${tab.id}`}
                  onClick={() => selectTab(tab.id)}
                  className={`px-3.5 sm:px-4 py-2 text-xs font-medium tracking-wide whitespace-nowrap rounded-full transition-all ${
                    active
                      ? 'bg-neon/15 text-neon border border-neon/40 shadow-[0_0_15px_rgba(74,222,128,0.25)]'
                      : 'text-zinc-400 hover:text-white border border-transparent'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab panel — two-column on lg+ */}
      <ScriptProvider resetKey={activeTab}>
        <main className="relative z-20 px-3 sm:px-4 lg:px-6 pb-16">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
            {/* Form column */}
            <div className="lg:col-span-7 xl:col-span-7">
              <div
                role="tabpanel"
                id={`panel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
                className="bg-white/[0.025] backdrop-blur-xl border border-white/10 rounded-2xl p-5 sm:p-7 lg:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]"
              >
                {activeTab === 'presuppositions' && <PresuppositionsTab />}
                {activeTab === 'embedded' && <EmbeddedCommandsTab />}
                {activeTab === 'modal' && <ModalOperatorsTab />}
                {activeTab === 'tiedowns' && <TieDownsTab />}
                {activeTab === 'pacing' && <PacingLeadingTab />}
                {activeTab === 'conditional' && <ConditionalBindsTab />}
                {activeTab === 'doublebinds' && <DoubleBindsTab />}
                {activeTab === 'objections' && <ObjectionHandlingTab />}
              </div>
            </div>

            {/* Output column */}
            <div className="lg:col-span-5 xl:col-span-5">
              <div className="lg:sticky lg:top-6">
                <OutputPanel />
              </div>
            </div>
          </div>

          <p className="text-center text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-600 mt-6 sm:mt-8">
            Powered by Claude Opus 4.7
          </p>
        </main>
      </ScriptProvider>
    </div>
  );
}
